import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MessageSquare, FileText, Wrench, Calendar, Users,
  Send, Search, RefreshCw, Phone, ChevronRight, X,
  Loader2, MailOpen, ArrowLeft, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import AutoHeroLeads from '@/features/comms/AutoHeroLeads';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_ROLES = ['super_admin', 'admin', 'service_manager', 'floor_manager'];

const TABS = [
  { key: 'quotes',   label: 'Quote Sent',       Icon: FileText,     hint: 'Quotes already sent to customers' },
  { key: 'autohero', label: 'Quotes To Be Sent', Icon: Sparkles,    hint: 'New enquiries awaiting a quote' },
  { key: 'repairs',  label: 'Repairs Replies',  Icon: Wrench,       hint: 'Invoice-repair SMS recipients' },
  { key: 'bookings', label: 'Bookings',         Icon: Calendar,     hint: 'Customers with active bookings' },
  { key: 'leads',    label: 'Meta Leads',       Icon: MessageSquare, hint: 'Facebook & Instagram messages' },
];

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}

export default function Comms() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('quotes');

  // Per-tab lists (cached)
  const [listByTab, setListByTab] = useState({ quotes: [], repairs: [], bookings: [], leads: [] });
  const [totalByTab, setTotalByTab] = useState({});
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedMobiles, setSelectedMobiles] = useState(new Set());
  const searchDebounce = useRef(null);
  const chatBodyRef = useRef(null);
  const chatPrevCount = useRef(0);
  const searchRef = useRef('');

  // Chat panel
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [followupBody, setFollowupBody] = useState('');
  const [sending, setSending] = useState(false);
  // Quote details for the Quotes tab (line items + exact SMS sent)
  const [quoteDetail, setQuoteDetail] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Bulk-send modal
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBody, setBulkBody] = useState('');
  const [bulkSending, setBulkSending] = useState(false);

  // ─── auth
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!r.ok) return navigate('/login');
        const u = await r.json();
        if (!ALLOWED_ROLES.includes(u.role)) {
          toast.error('Not authorised for Communications');
          return navigate('/');
        }
        setUser(u);
      } catch {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // ─── load list for current tab
  const loadList = useCallback(async (q = '', silent = false) => {
    // Auto Hero ("New Leads") renders its own component; Meta Leads is a
    // placeholder inbox (Meta integration pending) — skip the generic fetch
    // for both.
    if (tab === 'autohero' || tab === 'leads') { if (!silent) setListLoading(false); return; }
    if (!silent) setListLoading(true);
    try {
      const url = new URL(`${BACKEND_URL}/api/comms/list/${tab}`);
      if (q) url.searchParams.set('q', q);
      // iter153 polish — bumped default limits so total == DB count for
      // typical workshop sizes. Quotes/Bookings ~200-300; leads is heavier
      // so kept at 100 (searchable). SM can search to narrow down.
      url.searchParams.set('limit',
        tab === 'leads' ? '100' :
        tab === 'repairs' ? '500' :
        '500');
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) {
        // iter153 — Surface the real HTTP status + body preview so
        // production issues (e.g. 404 because backend not redeployed
        // yet with iter150+) are diagnosable from the toast.
        let detail = '';
        try {
          const j = await r.json();
          detail = j.detail || j.message || JSON.stringify(j).slice(0, 120);
        } catch {
          detail = (await r.text().catch(() => '')).slice(0, 120);
        }
        const err = new Error(`HTTP ${r.status}${detail ? ' — ' + detail : ''}`);
        err.status = r.status;
        throw err;
      }
      const data = await r.json();
      setListByTab((prev) => ({ ...prev, [tab]: data.items || [] }));
      setTotalByTab((prev) => ({ ...prev, [tab]: data.total }));
    } catch (e) {
      const suffix = e.status === 404
        ? ' — endpoint missing (backend not redeployed yet?)'
        : e.status === 401
          ? ' — session expired, please log in again'
          : e.status === 403
            ? " — not authorised (need SM/FM/Admin/Super Admin)"
            : '';
      if (!silent) toast.error(`Failed to load ${tab} — ${e.message}${suffix}`, { duration: 6000 });
    } finally {
      if (!silent) setListLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (user) loadList('');
  }, [user, tab, loadList]);

  // Debounced search
  useEffect(() => {
    if (!user) return;
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => loadList(search), 300);
    return () => clearTimeout(searchDebounce.current);
  }, [search, user, loadList]);

  // iter151 — Deep-link: /comms?mobile=+61... auto-opens that customer's
  // conversation (used by the top-right notification bell when a reply
  // arrives). Runs once per mobile-param change; strips the param after
  // consuming so browser back doesn't re-trigger.
  useEffect(() => {
    if (!user) return;
    const target = searchParams.get('mobile');
    if (!target) return;
    setActiveCustomer({
      mobile: target,
      mobile_display: target,
      name: 'Customer',
    });
    setFollowupBody('');
    loadConversation(target);
    // Strip the param so subsequent tab-switches don't fight the state.
    const next = new URLSearchParams(searchParams);
    next.delete('mobile');
    setSearchParams(next, { replace: true });
  }, [user, searchParams, setSearchParams]);

  // Clear selection when switching tabs
  useEffect(() => {
    setSelectedMobiles(new Set());
    setSearch('');
    setActiveCustomer(null);
    setQuoteDetail(null);
  }, [tab]);

  // Keep a ref of the current search for silent background polls.
  useEffect(() => { searchRef.current = search; }, [search]);

  // ─── load conversation for active customer
  const loadConversation = useCallback(async (mobile, silent = false) => {
    if (!mobile) return;
    if (!silent) setConversationLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/conversation?mobile=${encodeURIComponent(mobile)}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      const next = data.messages || [];
      setConversation((prev) => {
        // Skip the state update if nothing changed — keeps polling cheap
        // and avoids yanking the scroll position while the SM reads.
        if (prev.length === next.length &&
            prev[prev.length - 1]?.at === next[next.length - 1]?.at) {
          return prev;
        }
        return next;
      });
    } catch (e) {
      if (!silent) {
        toast.error(`Failed to load conversation — ${e.message}`);
        setConversation([]);
      }
    } finally {
      if (!silent) setConversationLoading(false);
    }
  }, []);

  // iter176 — LIVE CHAT: poll the OPEN conversation every 6s so inbound
  // customer replies (delivered via the CellCast webhook → sms_inbox) show
  // up automatically — no manual refresh. Silent = no spinner flicker; the
  // change-detection in loadConversation avoids re-render churn. Pauses
  // while the browser tab is hidden to save requests.
  useEffect(() => {
    if (!activeCustomer?.mobile) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadConversation(activeCustomer.mobile, true);
      }
    }, 6000);
    return () => clearInterval(id);
  }, [activeCustomer, loadConversation]);

  // iter176 — refresh the left list every 20s so unread badges light up
  // when a reply arrives for a customer OTHER than the open one.
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadList(searchRef.current, true);
    }, 20000);
    return () => clearInterval(id);
  }, [user, loadList]);

  // iter204 — count badge for "Quotes To Be Sent" (Auto Hero leads awaiting a
  // quote). The tab renders its own component, so fetch the count separately.
  useEffect(() => {
    if (!user) return undefined;
    const fetchLeadCount = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/comms/leads?limit=1`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setTotalByTab((prev) => ({ ...prev, autohero: d.total }));
        }
      } catch { /* noop */ }
    };
    fetchLeadCount();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchLeadCount();
    }, 20000);
    return () => clearInterval(id);
  }, [user]);

  // Auto-scroll the chat to the newest message when the count changes
  // (initial open, a reply arrives, or the SM sends one).
  useEffect(() => {
    const el = chatBodyRef.current;
    if (el && conversation.length !== chatPrevCount.current) {
      el.scrollTop = el.scrollHeight;
      chatPrevCount.current = conversation.length;
    }
  }, [conversation]);

  const openCustomer = (customer) => {
    setActiveCustomer(customer);
    setFollowupBody('');
    loadConversation(customer.mobile);
    loadQuoteDetail(customer.quote_id);
  };

  // iter177 — fetch the quote (line items + exact SMS sent) for a Quotes row.
  const loadQuoteDetail = useCallback(async (quoteId) => {
    if (!quoteId) { setQuoteDetail(null); return; }
    setQuoteLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`${r.status}`);
      setQuoteDetail(await r.json());
    } catch {
      setQuoteDetail(null);
    } finally {
      setQuoteLoading(false);
    }
  }, []);

  const sendFollowup = async () => {
    if (!activeCustomer || !followupBody.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mobile: activeCustomer.mobile, body: followupBody.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      if (data.status === 'dry_run') {
        toast.warning('SMS logged in dry-run mode (CellCast key not set).');
      } else {
        toast.success('Message sent');
      }
      setFollowupBody('');
      // Reload conversation to show the sent message
      loadConversation(activeCustomer.mobile);
    } catch (e) {
      toast.error(`Send failed — ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const toggleSelect = (mobile) => {
    setSelectedMobiles((prev) => {
      const next = new Set(prev);
      if (next.has(mobile)) next.delete(mobile); else next.add(mobile);
      return next;
    });
  };
  const selectAll = () => {
    const visible = listByTab[tab] || [];
    // iter153 — Only mobile-having rows are selectable for bulk SMS.
    setSelectedMobiles(new Set(visible.filter((c) => c.has_mobile !== false && c.mobile).map((c) => c.mobile)));
  };
  const clearSel = () => setSelectedMobiles(new Set());

  const openBulkSend = () => {
    if (selectedMobiles.size === 0) {
      toast.error('Select at least one customer');
      return;
    }
    setBulkBody('');
    setBulkOpen(true);
  };
  const sendBulk = async () => {
    if (!bulkBody.trim() || selectedMobiles.size === 0) return;
    setBulkSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/bulk-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mobiles: Array.from(selectedMobiles),
          body: bulkBody.trim(),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const msg = `Sent: ${data.sent} · Dry-run: ${data.dry_run} · Failed: ${data.failed}`;
      if (data.sent > 0 || data.dry_run > 0) toast.success(msg);
      else toast.warning(msg);
      setBulkOpen(false);
      setBulkBody('');
      setSelectedMobiles(new Set());
      loadList(search);
    } catch (e) {
      toast.error(`Bulk send failed — ${e.message}`);
    } finally {
      setBulkSending(false);
    }
  };

  const list = listByTab[tab] || [];
  const total = totalByTab[tab];

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 space-y-4" data-testid="comms-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-indigo-600" />
              Customer Comms
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Send quotes, chase approvals, and reply to customer messages — all in one place.
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 w-full max-w-3xl">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={`comms-tab-${t.key}`}
                className="flex items-center gap-1.5"
              >
                <t.Icon className="h-3.5 w-3.5" />
                {t.label}
                {t.key !== 'leads' && totalByTab[t.key] != null && (
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1.5" data-testid={`comms-tab-count-${t.key}`}>{totalByTab[t.key]}</Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-3">
              {t.key === 'autohero' ? (
                <AutoHeroLeads />
              ) : t.key === 'leads' ? (
                <Card className="p-10 flex flex-col items-center justify-center text-center min-h-[calc(100vh-320px)]" data-testid="comms-meta-placeholder">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-fuchsia-500 flex items-center justify-center mb-4 shadow-lg shadow-fuchsia-500/20">
                    <MessageSquare className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Meta messages land here</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-md">
                    Facebook &amp; Instagram (Messenger) chats from customers will appear in this inbox so you can reply without leaving AutoShopFlow.
                  </p>
                  <Badge variant="outline" className="mt-4 text-[11px] border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">
                    Meta integration coming soon
                  </Badge>
                  <button
                    onClick={() => navigate('/marketing-centre')}
                    className="mt-5 text-xs text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                    data-testid="comms-meta-to-marketing"
                  >
                    Looking to bulk-SMS all customers? Use the Marketing Centre →
                  </button>
                </Card>
              ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* LEFT — customer list */}
                <div className={`${activeCustomer ? 'hidden lg:block' : ''} lg:col-span-1`}>
                  <Card className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Search className="h-4 w-4 text-slate-400" />
                      <Input
                        placeholder={`Search ${t.label.toLowerCase()}…`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-8 text-sm"
                        data-testid={`comms-search-${t.key}`}
                      />
                      {search && (
                        <button
                          onClick={() => setSearch('')}
                          className="text-slate-400 hover:text-slate-700"
                          data-testid={`comms-search-clear-${t.key}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => loadList(search)}
                        className="text-slate-400 hover:text-slate-700"
                        title="Refresh"
                        data-testid={`comms-refresh-${t.key}`}
                      >
                        <RefreshCw className={`h-4 w-4 ${listLoading ? 'animate-spin' : ''}`} />
                      </button>
                    </div>

                    {/* Bulk-select bar */}
                    <div className="flex items-center gap-2 py-1.5 border-b border-slate-100 mb-1 text-xs">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={list.length > 0 && list.every((c) => selectedMobiles.has(c.mobile))}
                          onChange={(e) => (e.target.checked ? selectAll() : clearSel())}
                          className="h-3.5 w-3.5"
                          data-testid={`comms-select-all-${t.key}`}
                        />
                        <span className="text-slate-700">
                          {selectedMobiles.size > 0 ? `${selectedMobiles.size} selected` : 'Select all'}
                        </span>
                      </label>
                      {selectedMobiles.size > 0 && (
                        <>
                          <button
                            onClick={clearSel}
                            className="text-slate-500 hover:text-slate-800"
                            data-testid={`comms-clear-selection-${t.key}`}
                          >
                            clear
                          </button>
                          <Button
                            size="sm"
                            className="ml-auto h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
                            onClick={openBulkSend}
                            data-testid={`comms-bulk-send-${t.key}`}
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Send SMS ({selectedMobiles.size})
                          </Button>
                        </>
                      )}
                    </div>

                    <div className="space-y-1 max-h-[calc(100vh-320px)] overflow-y-auto pr-1" data-testid={`comms-list-${t.key}`}>
                      {listLoading && list.length === 0 && (
                        <div className="py-6 text-center text-slate-400 text-sm">
                          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-1" />
                          Loading…
                        </div>
                      )}
                      {!listLoading && list.length === 0 && (
                        <div className="py-6 text-center text-slate-400 text-sm">
                          {search ? 'No matches.' : `No customers in ${t.label.toLowerCase()}.`}
                        </div>
                      )}
                      {list.map((c) => {
                        const isActive = activeCustomer?.mobile === c.mobile;
                        const isSelected = selectedMobiles.has(c.mobile);
                        // iter153 — Some quotes / bookings don't have a
                        // mobile on file yet. Show them (so the count
                        // matches Job Cards) but disable the chat + SMS
                        // actions until SM adds a number.
                        const hasMobile = c.has_mobile !== false && !!c.mobile;
                        return (
                          <div
                            key={`${c.mobile || 'noph'}-${c.source_id || c.customer_id || ''}`}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition ${
                              isActive ? 'bg-indigo-50 ring-1 ring-indigo-300' : 'hover:bg-slate-50'
                            } ${isSelected ? 'ring-1 ring-indigo-200' : ''} ${hasMobile ? 'cursor-pointer' : 'opacity-70'}`}
                            onClick={() => hasMobile && openCustomer(c)}
                            data-testid={`comms-row-${t.key}-${c.mobile || 'noph'}-${c.source_id || ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!hasMobile}
                              onChange={(e) => { e.stopPropagation(); if (hasMobile) toggleSelect(c.mobile); }}
                              onClick={(e) => e.stopPropagation()}
                              className="h-3.5 w-3.5 flex-shrink-0"
                              data-testid={`comms-select-${t.key}-${c.mobile || 'noph'}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                                {c.unread_inbound > 0 && (
                                  <Badge className="bg-red-500 hover:bg-red-500 text-[10px] h-4 px-1.5">
                                    {c.unread_inbound}
                                  </Badge>
                                )}
                                {!hasMobile && (
                                  <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-amber-300 text-amber-700 bg-amber-50">
                                    no phone
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-500 font-mono truncate">{c.mobile_display || c.mobile}</p>
                              {c.last_inbound_body && (
                                <p className="text-[10px] text-indigo-700 truncate">
                                  ↩ {c.last_inbound_body}
                                </p>
                              )}
                            </div>
                            {hasMobile && <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>

                    {total > list.length && (
                      <p className="text-[10px] text-slate-400 mt-2 text-center">
                        Showing {list.length} of {total.toLocaleString()} — refine the search to see more
                      </p>
                    )}
                  </Card>
                </div>

                {/* RIGHT — chat panel */}
                <div className={`${activeCustomer ? '' : 'hidden lg:block'} lg:col-span-2`}>
                  <Card className="p-3 flex flex-col h-[calc(100vh-260px)]">
                    {!activeCustomer ? (
                      <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                        Select a customer from the list to start a conversation.
                      </div>
                    ) : (
                      <>
                        {/* Chat header */}
                        <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                          <button
                            className="lg:hidden text-slate-500 hover:text-slate-900"
                            onClick={() => setActiveCustomer(null)}
                            data-testid="comms-close-chat"
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-900 truncate">{activeCustomer.name}</h3>
                              <Badge variant="outline" className="text-[10px]">{tab}</Badge>
                            </div>
                            <p className="text-xs text-slate-500 font-mono flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {activeCustomer.mobile_display || activeCustomer.mobile}
                            </p>
                          </div>
                          <span className="hidden sm:flex items-center gap-1.5 mr-1" data-testid="comms-live-indicator" title="Live — customer replies appear automatically">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                            </span>
                            <span className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">Live</span>
                          </span>
                          <button
                            onClick={() => loadConversation(activeCustomer.mobile)}
                            className="text-slate-400 hover:text-slate-700"
                            title="Refresh"
                            data-testid="comms-chat-refresh"
                          >
                            <RefreshCw className={`h-4 w-4 ${conversationLoading ? 'animate-spin' : ''}`} />
                          </button>
                        </div>

                        {/* Quote details (Quotes tab) — line items + exact SMS sent */}
                        {tab === 'quotes' && (quoteLoading || quoteDetail) && (
                          <div className="border border-amber-200 bg-amber-50/60 rounded-md p-2 mt-2 max-h-52 overflow-y-auto" data-testid="comms-quote-detail">
                            {quoteLoading ? (
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading quote…
                              </div>
                            ) : quoteDetail ? (
                              <>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <FileText className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                    <span className="text-sm font-bold text-slate-900 truncate">{quoteDetail.quote_number}</span>
                                    <Badge variant="outline" className="text-[10px] capitalize">{quoteDetail.status}</Badge>
                                  </div>
                                  <span className="text-sm font-black text-slate-900 tabular-nums" data-testid="comms-quote-total">
                                    ${Number(quoteDetail.total || 0).toFixed(2)}
                                  </span>
                                </div>
                                <div className="space-y-0.5 mb-1.5" data-testid="comms-quote-line-items">
                                  {(quoteDetail.line_items || []).map((li, i) => (
                                    <div key={li.line_id || i} className="flex items-center justify-between gap-2 text-[11px] text-slate-700">
                                      <span className="truncate">{li.quantity ? `${li.quantity}× ` : ''}{li.description}</span>
                                      <span className="tabular-nums text-slate-500 flex-shrink-0">${Number(li.line_total || 0).toFixed(2)}</span>
                                    </div>
                                  ))}
                                  {(!quoteDetail.line_items || quoteDetail.line_items.length === 0) && (
                                    <p className="text-[11px] text-slate-400">No line items on this quote.</p>
                                  )}
                                </div>
                                <div className="border-t border-amber-200 pt-1.5">
                                  <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-0.5">Exact SMS sent</p>
                                  {quoteDetail.last_sms_body ? (
                                    <p className="text-[11px] text-slate-600 whitespace-pre-wrap bg-white/70 rounded p-1.5 border border-slate-100" data-testid="comms-quote-sms">
                                      {quoteDetail.last_sms_body}
                                    </p>
                                  ) : (
                                    <p className="text-[11px] text-slate-400 italic" data-testid="comms-quote-sms">
                                      No SMS body recorded for this quote yet.
                                    </p>
                                  )}
                                </div>
                              </>
                            ) : null}
                          </div>
                        )}

                        {/* Messages */}
                        <div ref={chatBodyRef} className="flex-1 overflow-y-auto py-3 space-y-2" data-testid="comms-chat-body">
                          {conversationLoading && (
                            <div className="text-center text-slate-400 text-sm">
                              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                            </div>
                          )}
                          {!conversationLoading && conversation.length === 0 && (
                            <div className="text-center text-slate-400 text-sm py-6">
                              <MailOpen className="h-8 w-8 mx-auto mb-1 opacity-50" />
                              No messages yet. Send your first follow-up below.
                            </div>
                          )}
                          {conversation.map((m, i) => (
                            <div
                              key={i}
                              className={`flex ${m.direction === 'in' ? 'justify-start' : 'justify-end'}`}
                              data-testid={`comms-msg-${m.direction}-${i}`}
                            >
                              <div
                                className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                                  m.direction === 'in'
                                    ? 'bg-white border border-slate-200 text-slate-800'
                                    : 'bg-indigo-600 text-white'
                                }`}
                              >
                                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                                <p className={`text-[10px] mt-1 ${m.direction === 'in' ? 'text-slate-500' : 'text-indigo-200'}`}>
                                  {m.direction === 'in' ? '↩ Customer' : '→ ' + (m.source || 'sent')}
                                  {' · '}
                                  {fmtWhen(m.at)}
                                  {m.status && m.status !== 'sent' && m.status !== 'received' && (
                                    <span className="ml-1 uppercase font-semibold">
                                      · {m.status}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Composer */}
                        <div className="pt-2 border-t border-slate-200 space-y-2">
                          <Textarea
                            value={followupBody}
                            onChange={(e) => setFollowupBody(e.target.value)}
                            placeholder="Type a follow-up message… (Ctrl+Enter to send)"
                            rows={3}
                            className="text-sm resize-none"
                            data-testid="comms-followup-body"
                            onKeyDown={(e) => {
                              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                e.preventDefault();
                                if (!sending) sendFollowup();
                              }
                            }}
                          />
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] text-slate-500">
                              {followupBody.length} chars · sends via CellCast
                            </p>
                            <Button
                              size="sm"
                              onClick={sendFollowup}
                              disabled={sending || !followupBody.trim()}
                              className="bg-indigo-600 hover:bg-indigo-700"
                              data-testid="comms-followup-send"
                            >
                              {sending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                              Send
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </Card>
                </div>
              </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* Bulk send modal */}
        {bulkOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBulkOpen(false)}>
            <Card className="w-full max-w-lg p-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">
                  Bulk SMS to {selectedMobiles.size} customer{selectedMobiles.size === 1 ? '' : 's'}
                </h3>
                <button onClick={() => setBulkOpen(false)} className="text-slate-400 hover:text-slate-700">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <Textarea
                value={bulkBody}
                onChange={(e) => setBulkBody(e.target.value)}
                placeholder="Type your bulk message here…"
                rows={5}
                className="text-sm mb-2"
                data-testid="comms-bulk-body"
              />
              <p className="text-[11px] text-slate-500 mb-3">
                {bulkBody.length} chars · will be sent individually to each customer
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setBulkOpen(false)}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={sendBulk}
                  disabled={bulkSending || !bulkBody.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="comms-bulk-send-confirm"
                >
                  {bulkSending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                  Send to {selectedMobiles.size}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
